import React, { useState } from 'react';
import { AlertCircle, Bug, Lightbulb, LifeBuoy, X, ChevronUp } from 'lucide-react';

const SUPPORT_EMAIL = 'sawant.ds@gmail.com';

// Top-of-page sticky banner shown across website + portal until GA.
export const PublicBetaBanner = () => {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('pou_beta_dismissed') === '1'; } catch { return false; }
  });
  if (dismissed) return null;
  const close = () => {
    try { sessionStorage.setItem('pou_beta_dismissed', '1'); } catch {}
    setDismissed(true);
  };
  return (
    <div className="bg-gold text-navy text-xs sm:text-sm" data-testid="public-beta-banner">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <span className="inline-flex items-center font-bold">
          <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
          Public Beta
          <span className="ml-2 font-normal text-navy/80 hidden sm:inline">
            — actively evolving. Please share feedback via the widget below.
          </span>
        </span>
        <button onClick={close} className="text-navy/70 hover:text-navy" aria-label="Dismiss beta banner" data-testid="beta-banner-dismiss">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// Floating bottom-right widget for Report Issue / Suggest Feature / Contact Support.
// Uses mailto: so it works without a backend — no PII leakage, founder controls inbox.
export const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);

  const mailto = (subject) => {
    const body = encodeURIComponent(
      `\n\n--- Context ---\nURL: ${window.location.href}\nUser-Agent: ${navigator.userAgent}\nTimestamp: ${new Date().toISOString()}\n`
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  return (
    <div className="fixed bottom-4 left-4 z-40" data-testid="feedback-widget">
      {open && (
        <div className="mb-2 bg-white border-2 border-navy/20 rounded-lg shadow-xl overflow-hidden w-64" data-testid="feedback-widget-panel">
          <div className="bg-navy text-white px-3 py-2 text-[11px] font-bold flex items-center justify-between">
            <span>Public Beta — your feedback shapes us</span>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" aria-label="Close feedback panel" data-testid="feedback-widget-close">
              <X className="h-3 w-3" />
            </button>
          </div>
          <a href={mailto('[ProcureOne Ultra] Report Issue')} className="flex items-center px-3 py-2.5 text-xs text-navy hover:bg-navy/5 border-b border-border" data-testid="feedback-report-issue">
            <Bug className="h-3.5 w-3.5 mr-2 text-red-600" /> Report an Issue
          </a>
          <a href={mailto('[ProcureOne Ultra] Feature Suggestion')} className="flex items-center px-3 py-2.5 text-xs text-navy hover:bg-navy/5 border-b border-border" data-testid="feedback-suggest-feature">
            <Lightbulb className="h-3.5 w-3.5 mr-2 text-orange" /> Suggest a Feature
          </a>
          <a href={mailto('[ProcureOne Ultra] Support Request')} className="flex items-center px-3 py-2.5 text-xs text-navy hover:bg-navy/5" data-testid="feedback-contact-support">
            <LifeBuoy className="h-3.5 w-3.5 mr-2 text-green-700" /> Contact Support
          </a>
          <div className="px-3 py-1.5 bg-muted/40 text-[10px] text-muted-foreground">
            Replies to <strong>{SUPPORT_EMAIL}</strong>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="bg-navy text-gold rounded-full shadow-lg px-4 py-2 text-xs font-bold inline-flex items-center hover:bg-navy/90"
        data-testid="feedback-widget-toggle"
      >
        <LifeBuoy className="h-4 w-4 mr-1.5" /> Feedback
        <ChevronUp className={`h-3 w-3 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
};
